import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <img src="/icons/icon-192.png" alt="TradePilot" className="w-10 h-10 rounded-lg shadow-sm ring-1 ring-brand/10" />
          <span className="font-display font-semibold text-xl text-brand-ink">
            Trade<span className="text-brand">Pilot</span>
          </span>
        </Link>
        <h1 className="font-display text-4xl font-medium tracking-tight text-center">Welcome back</h1>
        <p className="text-center text-gray-500 mt-2 text-sm">Sign in to keep trading.</p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
            />
          </div>
          {err && (
            <p data-testid="login-error" className="text-sm text-loss">
              {err}
            </p>
          )}
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-brand-gradient text-white hover:opacity-90 font-medium shadow-sm"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-sm text-center text-gray-500 mt-8">
          New here?{" "}
          <Link to="/register" className="text-brand-deep font-medium hover:underline" data-testid="login-to-register">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
